// The game store: the games of the last 30 days, the window the whole app
// works on, held in memory once with their facts, kept current as games are
// imported, removed and scanned, and the registry of the cards' aggregators
// run over them. Older games stay in storage and are never read. Provided by plugin
// `shared-store` (`STORE_API`); read through `getApi`, never imported as a
// module, so every plugin sees the same instance. Writing stays with the
// APIs that own the data (`games`, `analysis`); the store follows their
// events. See docs/architecture/core/plugin-shared-store.md.
import type { GameRecord, GameSummary, GameId } from './game.ts';
import type { Scan } from './scan.ts';
import type { GameFacts } from './facts.ts';
import type { GameFilter } from './filter.ts';
import type { TimeClass } from './classification.ts';
import type { Aggregator, AggregatorId } from './aggregator.ts';

export const STORE_API = 'store';

export type StoreStatus = 'loading' | 'ready' | 'failed';

/** A game as the store holds it: no moves, no scan. */
export interface StoredGame {
  summary: GameSummary;
  /** The PGN's header block without the moves: what the filter reads (opening, ending). */
  headers: string;
  /** The game's facts; undefined for a game the reader played neither side of, or unfinished. */
  facts?: GameFacts;
}

/** The period's games and scans: the dashboard header's caption and the cards' coverage notes. */
export interface PeriodSummary {
  /** Epoch milliseconds, the period's start (exclusive) and end. */
  from: number;
  to: number;
  /** The reader's games in the period (finished, a side of theirs). */
  games: number;
  /** How many of them are scanned. */
  scanned: number;
}

/** The store's state: a new object on every change, so it can be compared by reference. */
export interface StoreState {
  status: StoreStatus;
  /** The last 30 days' games, newest first. */
  games: readonly StoredGame[];
  /** The period's games and scans; set once ready. */
  period?: PeriodSummary;
  /** Each registered aggregator's result; absent until it has run, or when it failed. */
  results: Readonly<Record<AggregatorId, unknown>>;
  /** Goes up on every change: a game imported, removed or rescanned, an aggregator registered. */
  revision: number;
}

/** Which games `list` returns, and in what order. */
export interface GameQuery {
  /** Only this account's games. */
  owner?: string;
  /** Only games matching this filter; `blunder`, `endingType`, `endingResult`, `middlegameResult`, `groundCause`, `middlegamePosition`, `conversion`, `stageChange` and `middlegameTurn` must be resolved first (`resolveFilter`). Always within the period: the store holds no other games. */
  filter?: GameFilter;
  /** Newest first by default. */
  order?: 'newest' | 'oldest';
  /** Skip this many games of the ordered list; 0 by default. */
  offset?: number;
  /** At most this many games from `offset` on; all of them by default. */
  size?: number;
}

export interface GameStore {
  getState(): StoreState;
  /** Hears every change of state. Returns the unsubscribe. */
  subscribe(listener: (state: StoreState) => void): () => void;
  /** Settles once the first load has finished, loaded or failed. */
  ready(): Promise<void>;

  /** One game as held, or undefined. */
  game(id: GameId): StoredGame | undefined;
  /** The games matching the query: summaries, from memory. */
  list(query?: GameQuery): GameSummary[];
  /** How many games `list` would return with the same owner and filter. */
  count(query?: Pick<GameQuery, 'owner' | 'filter'>): number;
  /**
   * The filter with its scan-fact criteria resolved into `ids`: `blunder`
   * (games with any of its kinds of blunder), `endingType` (games whose
   * ending was of any of its kinds), `endingResult` (games whose ending
   * result is any of its values), `middlegameResult` (the same for the
   * middlegame), `groundCause` (games whose middlegame went wrong for any
   * of its causes), `middlegamePosition` (games whose middlegame the reader
   * played in any of its kinds of position), `conversion` (games in any of its
   * sets of a middlegame advantage, `conversionsOf`) and `stageChange`, all that are set;
   * scanned games only. Ready for `list` and `count`; any other filter comes back as it is.
   */
  resolveFilter(filter: GameFilter): GameFilter;

  /** The whole record with its PGN, read from storage; not held in memory. */
  record(id: GameId): Promise<GameRecord | undefined>;
  /** The game's stored scan, read from storage; not held in memory. */
  scan(id: GameId): Promise<Scan | undefined>;

  /**
   * Adds a card's aggregator; it runs over the period's games from now on.
   * Registering the same id again (a plugin reloaded) replaces the earlier
   * one; an id several plugins register stays until the last unregisters.
   * Returns the unregister.
   */
  registerAggregator<R>(aggregator: Aggregator<R>): () => void;
  /** The aggregator's result; undefined until it has run, or when it failed. */
  result<R>(aggregator: Aggregator<R>): R | undefined;
}
